import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, X } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DateField, TimeField } from "@/components/DateTimeField";
import { CrewSelect } from "@/components/CrewSelect";
import { ARRIVAL_WINDOWS } from "@/lib/scheduleWindows";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useDirectory } from "@/lib/directory";

const defaultsFor = (t) =>
  (t?.default_fields || []).map((d) => ({
    label: d.label || "",
    value: d.value || "",
  }));

const blank = (types) => {
  const t0 = types[0];
  return {
    kind: t0?.label || "",
    color: t0?.color || "",
    label: "",
    date: "",
    time: "",
    window: "All day",
    crew: "",
    areas: [],
    custom_fields: defaultsFor(t0),
    notes: [],
  };
};

export function TaskDialog({
  open,
  onOpenChange,
  resourcePath,
  areasSource,
  task,
  taskTypes,
  crewList,
  onSaved,
  noun = "task",
}) {
  const { nameOf } = useDirectory();
  const [f, setF] = useState(blank(taskTypes));
  const [areaInput, setAreaInput] = useState("");
  const [newNote, setNewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [suggested, setSuggested] = useState([]);

  useEffect(() => {
    if (!open || !areasSource) return;
    api
      .get(areasSource)
      .then(({ data }) => setSuggested(data.areas || []))
      .catch(() => setSuggested([]));
  }, [open, areasSource]);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setF({
        kind: task.kind || "",
        color: task.color || "",
        label: task.label || "",
        date: task.date || "",
        time: task.time || "",
        window: task.window || "All day",
        crew: task.crew || "",
        areas: task.areas || [],
        custom_fields: task.custom_fields || [],
        notes: task.notes || [],
      });
    } else {
      setF(blank(taskTypes));
    }
    setAreaInput("");
    setNewNote("");
  }, [open, task, taskTypes]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const pickType = (t) =>
    setF((p) => {
      const next = { ...p, kind: t.label, color: t.color || "" };
      // On a new task, pre-fill this type's default fields (unless the user
      // already typed a value they'd lose).
      if (!task) {
        const hasUserData = p.custom_fields.some((c) => (c.value || "").trim());
        if (!hasUserData) next.custom_fields = defaultsFor(t);
      }
      return next;
    });
  const addAreaValue = (v) => {
    const name = (v || "").trim();
    if (!name) return;
    setF((p) =>
      p.areas.includes(name) ? p : { ...p, areas: [...p.areas, name] },
    );
  };
  const addArea = () => {
    addAreaValue(areaInput);
    setAreaInput("");
  };
  const removeArea = (i) =>
    setF((p) => ({ ...p, areas: p.areas.filter((_, x) => x !== i) }));
  const addField = () =>
    setF((p) => ({
      ...p,
      custom_fields: [...p.custom_fields, { label: "", value: "" }],
    }));
  const setField = (i, k, v) =>
    setF((p) => ({
      ...p,
      custom_fields: p.custom_fields.map((c, x) =>
        x === i ? { ...c, [k]: v } : c,
      ),
    }));
  const removeField = (i) =>
    setF((p) => ({
      ...p,
      custom_fields: p.custom_fields.filter((_, x) => x !== i),
    }));

  const save = async () => {
    if (!f.kind) {
      toast.error("Pick a task type");
      return;
    }
    const notes = [...f.notes];
    if (newNote.trim()) notes.push({ body: newNote.trim() });
    const payload = {
      kind: f.kind,
      color: f.color,
      label: f.label,
      date: f.date,
      time: f.time,
      window: f.window || "All day",
      crew: f.crew,
      areas: f.areas,
      custom_fields: f.custom_fields.filter(
        (c) => (c.label || "").trim() || (c.value || "").trim(),
      ),
      notes,
    };
    setSaving(true);
    try {
      const { data } = task
        ? await api.patch(`${resourcePath}/${task.id}`, payload)
        : await api.post(resourcePath, payload);
      onSaved(data);
      onOpenChange(false);
      toast.success(task ? "Task updated" : "Task added");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not save task",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-zinc-900 sm:max-w-2xl"
        data-testid="task-dialog"
      >
        <DialogHeader>
          <DialogTitle>{task ? `Edit ${noun}` : `New ${noun}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="mb-1.5 block text-xs uppercase tracking-widest text-zinc-500">
              Type
            </div>
            <div className="flex flex-wrap gap-2" data-testid="task-type-options">
              {taskTypes.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`task-type-${t.id}`}
                  onClick={() => pickType(t)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest transition-colors duration-150 ${
                    f.kind === t.label
                      ? "border-transparent text-black"
                      : "border-zinc-700 text-zinc-300 hover:border-zinc-500"
                  }`}
                  style={f.kind === t.label ? { backgroundColor: t.color } : {}}
                >
                  <span
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: t.color }}
                  />
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
                Date
              </div>
              <DateField testId="task-date" value={f.date} onChange={(v) => set("date", v)} />
            </div>
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
                Window
              </div>
              <select
                data-testid="task-window"
                value={f.window}
                onChange={(e) => set("window", e.target.value)}
                className="h-10 w-full rounded border border-zinc-800 bg-zinc-950 px-2 text-sm text-zinc-200"
              >
                {[...new Set([f.window || "All day", "All day", ...ARRIVAL_WINDOWS])].map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
                Time (optional)
              </div>
              <TimeField testId="task-time" value={f.time} onChange={(v) => set("time", v)} />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
                Label
              </div>
              <Input
                data-testid="task-label"
                value={f.label}
                onChange={(e) => set("label", e.target.value)}
                placeholder="e.g. Units 203–205"
                className="h-10 border-zinc-800 bg-zinc-950"
              />
            </div>
            <div>
              <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
                Crew
              </div>
              <CrewSelect
                testid="task-crew"
                value={f.crew}
                onChange={(v) => set("crew", v)}
                crewList={crewList}
              />
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
              Areas
            </div>
            <div className="flex gap-2">
              <Input
                data-testid="task-area-input"
                value={areaInput}
                onChange={(e) => setAreaInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addArea();
                  }
                }}
                placeholder="Add an area (e.g. Kitchen)"
                className="h-10 border-zinc-800 bg-zinc-950"
              />
              <Button
                type="button"
                data-testid="task-area-add"
                onClick={addArea}
                variant="outline"
                className="h-10 border-zinc-700 bg-transparent"
              >
                Add
              </Button>
            </div>
            {suggested.filter((a) => !f.areas.includes(a)).length > 0 && (
              <div className="mt-2" data-testid="task-area-suggestions">
                <div className="mb-1 text-[10px] uppercase tracking-widest text-zinc-600">
                  From drawing
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {suggested
                    .filter((a) => !f.areas.includes(a))
                    .map((a) => (
                      <button
                        key={a}
                        type="button"
                        data-testid={`task-area-suggest-${a}`}
                        onClick={() => addAreaValue(a)}
                        className="inline-flex items-center gap-1 rounded-full border border-dashed border-zinc-600 px-2 py-0.5 text-xs text-zinc-300 transition-colors duration-150 hover:border-indigo-400 hover:text-indigo-200"
                      >
                        <Plus size={11} weight="bold" /> {a}
                      </button>
                    ))}
                </div>
              </div>
            )}
            {f.areas.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="task-areas">
                {f.areas.map((a, i) => (
                  <span
                    key={`${a}-${i}`}
                    className="inline-flex items-center gap-1 rounded-full border border-indigo-500/30 bg-indigo-500/10 px-2 py-0.5 text-xs text-indigo-200"
                  >
                    {a}
                    <button
                      type="button"
                      onClick={() => removeArea(i)}
                      className="text-indigo-300 hover:text-white"
                    >
                      <X size={12} weight="bold" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-zinc-500">
                Custom fields
              </span>
              <button
                type="button"
                data-testid="task-field-add"
                onClick={addField}
                className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"
              >
                <Plus size={12} weight="bold" /> Add field
              </button>
            </div>
            <div className="space-y-2" data-testid="task-fields">
              {f.custom_fields.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    data-testid={`task-field-label-${i}`}
                    value={c.label}
                    onChange={(e) => setField(i, "label", e.target.value)}
                    placeholder="Field"
                    className="h-9 border-zinc-800 bg-zinc-950"
                  />
                  <Input
                    data-testid={`task-field-value-${i}`}
                    value={c.value}
                    onChange={(e) => setField(i, "value", e.target.value)}
                    placeholder="Value"
                    className="h-9 border-zinc-800 bg-zinc-950"
                  />
                  <button
                    type="button"
                    onClick={() => removeField(i)}
                    className="text-zinc-500 hover:text-red-400"
                  >
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="mb-1.5 text-xs uppercase tracking-widest text-zinc-500">
              Team notes
            </div>
            {f.notes.length > 0 && (
              <div className="mb-2 space-y-1" data-testid="task-notes">
                {f.notes.map((n, i) => (
                  <div
                    key={n.id || i}
                    className="rounded border border-zinc-800 bg-zinc-950 p-2 text-xs text-zinc-300"
                  >
                    {n.body}
                    <span className="mt-0.5 block text-[10px] text-zinc-600">
                      {nameOf(n.by)}
                      {n.at ? ` · ${new Date(n.at).toLocaleDateString()}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
            <Textarea
              data-testid="task-note-input"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note for the crew…"
              className="min-h-[64px] border-zinc-800 bg-zinc-950"
            />
          </div>
        </div>

        <div className="mt-2 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="task-cancel"
            onClick={() => onOpenChange(false)}
            className="border-zinc-700 bg-transparent"
          >
            Cancel
          </Button>
          <Button
            type="button"
            data-testid="task-save"
            onClick={save}
            disabled={saving}
            className="bg-indigo-500 hover:bg-indigo-400"
          >
            {task ? `Save ${noun}` : `Add ${noun}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
